import { MutationFunction, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSignalR } from "src/hooks";
import { queryKeys, TaskItemDetailDto } from "src/http";

interface TaskItemMutationVariables {
  taskItemGuid: string;
}

interface Context {
  cachedDetailData: TaskItemDetailDto | undefined;
}

interface UseTaskItemMutationOptions<Variables> {
  mutationFn: MutationFunction<unknown, Variables>;
  onMutate?: (variables: Variables) => Context | Promise<Context | undefined> | undefined;
  onSuccess?: (data: unknown, variables: Variables, context: Context | undefined) => Promise<unknown> | unknown;
  onError?: (error: unknown, variables: Variables, context: Context | undefined) => Promise<unknown> | unknown;
  onSettled?: (
    data: unknown,
    error: unknown,
    variables: Variables,
    context: Context | undefined
  ) => Promise<unknown> | unknown;
}

const useTaskItemMutation = <Variables extends TaskItemMutationVariables>(
  options: UseTaskItemMutationOptions<Variables>
) => {
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation<unknown, unknown, Variables, Context>({
    mutationFn: options.mutationFn,
    onMutate: async (variables) => {
      return await options.onMutate?.(variables);
    },
    onSuccess: (data, variables, context) => {
      signalR.sendRefetchTaskItem(variables.taskItemGuid);
      signalR.sendRefetchComments(variables.taskItemGuid);
      signalR.sendRefetchTeamTaskItems();
      signalR.sendRefetchUserTaskItems();

      options.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        context?.cachedDetailData
      );

      options.onError?.(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid));
      queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemFeed(variables.taskItemGuid));

      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);

      options.onSettled?.(data, error, variables, context);
    },
  });
};

export { useTaskItemMutation };
